import { useI18n } from '../../i18n';
import type { ChipTone } from '../../lib/tokens';

/**
 * Job/asset status chip (§5.6). State is NEVER color alone: each tone pairs a drawn glyph
 * + a word. The "review" tone is a task and may be clickable.
 */
interface StatusChipProps {
  tone: ChipTone;
  onClick?: () => void;
  className?: string;
}

const TONE_CLASSES: Record<ChipTone, string> = {
  pending: 'text-secondary border-border-strong bg-surface-2',
  processing: 'text-info border-info bg-info-tint',
  review: 'text-warn border-warn bg-warn-tint',
  complete: 'text-success border-success bg-success-tint',
  failed: 'text-danger border-danger bg-danger-tint',
  sold: 'text-success border-success bg-success-tint',
  neutral: 'text-muted border-border-strong bg-surface-2',
};

function Glyph({ tone }: { tone: ChipTone }) {
  const common = { width: 12, height: 12, viewBox: '0 0 12 12', 'aria-hidden': true, focusable: false } as const;
  switch (tone) {
    case 'pending':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.5}>
          <circle cx="6" cy="6" r="4" />
        </svg>
      );
    case 'processing':
      return (
        <svg {...common} className="pulse-signal" fill="currentColor">
          <path d="M3 2v8l7-4-7-4Z" />
        </svg>
      );
    case 'review':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round">
          <path d="M6 1 11 10.5H1L6 1Z" />
          <path d="M6 5v2.5M6 9v.01" strokeLinecap="round" />
        </svg>
      );
    case 'complete':
    case 'sold':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 6.5 5 9.5 10 3" />
        </svg>
      );
    case 'failed':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
          <path d="M3 3l6 6M9 3 3 9" />
        </svg>
      );
    default:
      return null;
  }
}

export function StatusChip({ tone, onClick, className = '' }: StatusChipProps) {
  const { t } = useI18n();
  const label: Record<ChipTone, string> = {
    pending: t.states.pending,
    processing: t.states.processing,
    review: t.states.review,
    complete: t.states.complete,
    failed: t.states.failed,
    sold: t.states.sold,
    neutral: '—',
  };

  const content = (
    <>
      <Glyph tone={tone} />
      <span>{label[tone]}</span>
    </>
  );

  const cls = [
    'inline-flex h-6 items-center gap-1.5 rounded-sm border px-2 type-label',
    TONE_CLASSES[tone],
    className,
  ].join(' ');

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} focus-ring transition-colors duration-fast ease-machine hover:brightness-110`}>
        {content}
      </button>
    );
  }
  return (
    <span className={cls} role="status">
      {content}
    </span>
  );
}
